// src/pages/SuperAdminModuleUploader.jsx

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';
import { UploadCloud, Package, FileArchive, CheckCircle2, Loader2, AlertCircle, BookOpen, Layers, Database } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

const SuperAdminModuleUploader = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [uploadResult, setUploadResult] = useState(null);

  const handleUpload = async () => {
    if (!file) {
      toast.error('Por favor, selecciona un paquete SCORM para subir.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);
    setUploadStage('Validando paquete SCORM...');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 85) { clearInterval(progressInterval); return 85; }
          return prev + 3;
        });
        setUploadStage((prev) => {
          if (uploadProgress > 20) return 'Extrayendo contenido de las unidades...';
          if (uploadProgress > 40) return 'Leyendo PDFs de respuestas...';
          if (uploadProgress > 60) return 'Creando sub-módulos y tests...';
          if (uploadProgress > 75) return 'Guardando en base de datos...';
          return prev;
        });
      }, 600);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/superadmin/upload-scorm-with-units`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al procesar el paquete SCORM.');
      }

      setUploadProgress(100);
      setUploadStage('¡Curso importado con éxito!');

      const result = await response.json();
      setUploadResult(result);

      toast.success(
        `✅ "${result.parent_module_title}" importado — ${result.submodules_created} unidades · ${result.tests_created} tests`
      );

      setTimeout(() => navigate('/superadmin/modules'), 2500);

    } catch (error) {
      console.error('Error en la subida:', error);
      toast.error(error.message);
      setUploadProgress(0);
      setUploadStage('');
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      toast.error('Solo se aceptan archivos ZIP (.zip).');
      return;
    }
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
        toast.error('El archivo debe ser un ZIP (.zip).');
        return;
      }
      setFile(selectedFile);
      setUploadResult(null);
      toast.success(`Paquete "${selectedFile.name}" seleccionado.`);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    },
    maxFiles: 1,
    maxSize: 524288000, // 500 MB
  });

  const removeFile = () => {
    setFile(null);
    setUploadProgress(0);
    setUploadStage('');
    setUploadResult(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Layers className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Importar Módulo SCORM</h1>
        </div>
        <p className="text-muted-foreground ml-14">
          Sube el ZIP maestro del curso (contiene UNIDAD_1.zip … UNIDAD_9.zip + RESPUESTAS TEST UNIDAD X.pdf).
          Se crearán automáticamente las unidades y sus tests oficiales.
        </p>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { icon: BookOpen,     label: 'Unidades por ZIP',   desc: '1 sub-módulo por cada UNIDAD_X.zip' },
          { icon: Database,     label: 'Tests desde PDFs',   desc: 'Preguntas extraídas sin IA' },
          { icon: CheckCircle2, label: 'Validación',          desc: 'Verifica la estructura del paquete' },
        ].map(({ icon: Icon, label, desc }) => (
          <div key={label} className="bg-card border border-border rounded-xl p-4 text-center">
            <Icon className="w-5 h-5 text-primary mx-auto mb-2" />
            <p className="text-xs font-semibold">{label}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
          </div>
        ))}
      </div>

      {/* Main card */}
      <div className="bg-card border border-border rounded-xl p-8 space-y-6">

        {/* Drop zone / file preview */}
        {file ? (
          <div className="relative bg-primary/5 border border-primary/20 rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-xl">
                <FileArchive className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatFileSize(file.size)} · ZIP maestro SCORM
                </p>
                <div className="flex items-center gap-1.5 mt-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-xs text-green-600 dark:text-green-400">Archivo válido</span>
                </div>
              </div>
              {!isUploading && (
                <button
                  onClick={removeFile}
                  className="text-xs text-muted-foreground hover:text-destructive transition-colors underline underline-offset-2"
                >
                  Quitar
                </button>
              )}
            </div>

            {/* Progress bar */}
            {isUploading && (
              <div className="mt-5 space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{uploadStage}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Result block */}
            {uploadResult && !isUploading && (
              <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl space-y-2">
                <p className="font-semibold text-green-700 dark:text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Curso creado: "{uploadResult.parent_module_title}"
                </p>
                <div className="flex gap-6 text-xs text-muted-foreground">
                  <span>{uploadResult.submodules_created} unidades creadas</span>
                  <span>{uploadResult.tests_created} tests desde PDFs oficiales</span>
                </div>
                {uploadResult.submodules?.length > 0 && (
                  <div className="grid grid-cols-3 gap-1.5 mt-2">
                    {uploadResult.submodules.map(s => (
                      <div key={s.id}
                        className="flex items-center gap-1.5 px-2 py-1 bg-background rounded border text-xs">
                        <span className="w-4 h-4 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {s.unit_number}
                        </span>
                        <span className="truncate text-muted-foreground">{s.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={`relative p-14 border-2 border-dashed rounded-xl text-center cursor-pointer transition-all duration-200 group ${
              isDragActive
                ? 'border-primary bg-primary/5 scale-[1.01]'
                : 'border-border hover:border-primary/50 hover:bg-muted/30'
            }`}
          >
            <input {...getInputProps()} />
            <div className={`relative mx-auto mb-5 w-16 h-16 transition-transform duration-200 ${isDragActive ? 'scale-110' : 'group-hover:scale-105'}`}>
              <div className="absolute inset-0 bg-primary/10 rounded-2xl rotate-6 transition-transform duration-200 group-hover:rotate-12" />
              <div className="relative flex items-center justify-center w-full h-full bg-primary/10 rounded-2xl">
                <UploadCloud className={`w-7 h-7 text-primary transition-all ${isDragActive ? 'animate-bounce' : ''}`} />
              </div>
            </div>

            {isDragActive ? (
              <p className="text-lg font-semibold text-primary">Suelta el ZIP aquí...</p>
            ) : (
              <>
                <p className="text-base font-medium mb-1">Arrastra el ZIP maestro del curso aquí</p>
                <p className="text-sm text-muted-foreground">
                  o{' '}
                  <span className="text-primary font-semibold underline underline-offset-2">
                    haz clic para seleccionar
                  </span>
                </p>
              </>
            )}

            <div className="mt-5 inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted/60 rounded-lg px-3 py-1.5">
              <Package className="w-3.5 h-3.5" />
              <span>ZIP con UNIDAD_X.zip + RESPUESTAS TEST UNIDAD X.pdf · Máx. 500 MB</span>
            </div>
          </div>
        )}

        {/* Structure hint */}
        {!file && (
          <div className="flex gap-2.5 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-700 dark:text-amber-400">
              <p className="font-semibold mb-0.5">Estructura del ZIP maestro requerida</p>
              <p>
                El ZIP debe contener: <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">UNIDAD_1.zip</code> …{' '}
                <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">UNIDAD_9.zip</code> (SCORMs de cada unidad) y{' '}
                <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">RESPUESTAS TEST UNIDAD 1.pdf</code> …{' '}
                <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">RESPUESTAS TEST UNIDAD 9.pdf</code> (preguntas oficiales).
              </p>
            </div>
          </div>
        )}

        {/* Upload button */}
        <button
          onClick={handleUpload}
          disabled={!file || isUploading}
          className="w-full px-6 py-3.5 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2.5 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Importando curso SCORM…
            </>
          ) : (
            <>
              <UploadCloud className="w-5 h-5" />
              Importar Curso con Unidades
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SuperAdminModuleUploader;